import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useUpload } from '../context/UploadContext';

export default function InvoiceUpload() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  // 'Invoice' or 'CreditNote' from ?type= query param
  const documentType = searchParams.get('type') === 'CreditNote' ? 'CreditNote' : 'Invoice';
  const isCreditNote = documentType === 'CreditNote';
  const {
    jobs,
    isUploading,
    lastBatchResults,
    enqueueFiles,
    startUpload,
    clearCompleted,
    clearAll,
    doneCount,
    failedCount,
    totalCount,
    activeCount,
  } = useUpload();

  const [stagedFiles, setStagedFiles] = useState([]);
  const [dragActive, setDragActive] = useState(false);

  // Auto-start upload once files are enqueued
  useEffect(() => {
    if (jobs.some(j => j.status === 'pending') && !isUploading) {
      startUpload();
    }
  }, [jobs, isUploading, startUpload]);

  // Handle file selection
  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    addFiles(selectedFiles);
  };

  // Handle drag & drop
  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    const pdfFiles = droppedFiles.filter(f => f.type === 'application/pdf' || f.type.startsWith('image/'));
    addFiles(pdfFiles);
  }, []);

  // Add files to staged queue (before sending to context)
  const addFiles = (newFiles) => {
    const validFiles = newFiles.filter(f => {
      const isValid = f.type === 'application/pdf' || f.type.startsWith('image/');
      const isSmall = f.size <= 10 * 1024 * 1024; // 10MB
      return isValid && isSmall;
    });
    setStagedFiles(prev => [...prev, ...validFiles]);
  };

  // Remove file from staged queue
  const removeFile = (idx) => {
    setStagedFiles(prev => prev.filter((_, i) => i !== idx));
  };

  // Submit staged files → enqueue into global upload context
  const handleUpload = () => {
    if (stagedFiles.length === 0) return;
    enqueueFiles(stagedFiles, { documentType });
    setStagedFiles([]);
  };

  // Convenience: both batch results and in-flight job data
  const uploadResults = lastBatchResults;

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">
          {isCreditNote ? 'Upload Credit Notes' : 'Upload Invoices'}
        </h1>
        <p className="mt-2 text-gray-600">
          {isCreditNote
            ? 'Upload PDF or image credit note files. AI will extract data and link them to original invoices.'
            : 'Upload PDF or image files. AI will automatically extract invoice data.'}
        </p>
        {isCreditNote && (
          <div className="mt-3 flex items-start gap-2 bg-teal-50 border border-teal-200 rounded-lg px-4 py-3">
            <span className="text-teal-600 font-medium text-sm">Credit Note mode:</span>
            <span className="text-teal-700 text-sm">All uploaded files will be processed as credit notes. After upload, link each credit note to its original invoice.</span>
          </div>
        )}
      </div>

      {/* Upload Area */}
      <div className="bg-white rounded-lg shadow-lg p-8 mb-6">
        {/* Drag & Drop Zone */}
        <div
          className={`border-2 border-dashed rounded-lg p-12 text-center transition-colors ${
            dragActive 
              ? 'border-blue-500 bg-blue-50' 
              : 'border-gray-300 hover:border-gray-400'
          }`}
          onDragEnter={handleDrag}
          onDragOver={handleDrag}
          onDragLeave={handleDrag}
          onDrop={handleDrop}
        >
          <svg 
            className="mx-auto h-16 w-16 text-gray-400 mb-4" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth="2" 
              d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
            />
          </svg>
          
          <p className="text-lg font-semibold text-gray-700 mb-2">
            Drag and drop invoice files here
          </p>
          <p className="text-sm text-gray-500 mb-4">
            or click to browse (PDF, PNG, JPEG - max 10MB each)
          </p>
          
          <label className="inline-block">
            <input
              type="file"
              multiple
              accept=".pdf,image/png,image/jpeg"
              onChange={handleFileSelect}
              className="hidden"
            />
            <span className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer inline-block">
              Select Files
            </span>
          </label>
        </div>

        {/* File List — staged files before upload */}
        {stagedFiles.length > 0 && (
          <div className="mt-6">
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              Files to Upload ({stagedFiles.length})
              {stagedFiles.length === 1 && (
                <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-amber-100 text-amber-700 flex items-center gap-1">
                  ⚡ Priority
                </span>
              )}
            </h3>
            <div className="space-y-2">
              {stagedFiles.map((file, idx) => (
                <div 
                  key={idx}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center space-x-3">
                    <svg className="h-8 w-8 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z"/>
                    </svg>
                    <div>
                      <p className="font-medium text-gray-900">{file.name}</p>
                      <p className="text-sm text-gray-500">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => removeFile(idx)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                    </svg>
                  </button>
                </div>
              ))}
            </div>

            {/* Upload Button */}
            <div className="mt-6 flex gap-3">
              <button
                onClick={handleUpload}
                disabled={isUploading}
                className="btn btn-success btn-lg"
              >
                {isUploading ? 'Upload in progress...' : `Upload & Process ${stagedFiles.length} File(s)`}
              </button>
              <button
                onClick={() => setStagedFiles([])}
                className="btn btn-secondary btn-lg"
              >
                Clear All
              </button>
            </div>
          </div>
        )}

        {/* Global upload progress — shows jobs from context */}
        {isUploading && (
          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <div className="flex items-center space-x-3">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              <div>
                <p className="font-semibold text-blue-900">
                  Processing invoices... ({doneCount + failedCount} / {totalCount})
                </p>
                <p className="text-sm text-blue-700">
                  AI is extracting invoice data. You can safely navigate to other pages — uploads continue in the background.
                </p>
              </div>
            </div>
            {/* Per-job status */}
            <div className="mt-3 space-y-1">
              {jobs.map(job => (
                <div key={job.id} className="flex items-center gap-2 text-sm">
                  {(job.status === 'uploading')   && <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600" />}
                  {(job.status === 'queued')       && <div className="h-3 w-3 rounded-full bg-amber-400" title="Queued — waiting for OCR processor" />}
                  {(job.status === 'processing')   && <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-indigo-500" />}
                  {job.status === 'done'           && <span className="text-green-600">✓</span>}
                  {job.status === 'failed'         && <span className="text-red-600">✗</span>}
                  {job.status === 'pending'        && <span className="text-gray-400">○</span>}
                  <span className={
                    job.status === 'done'       ? 'text-green-700' :
                    job.status === 'failed'     ? 'text-red-700' :
                    job.status === 'uploading'  ? 'text-blue-700 font-medium' :
                    job.status === 'queued'     ? 'text-amber-600' :
                    job.status === 'processing' ? 'text-indigo-700 font-medium' :
                    'text-gray-500'
                  }>
                    {job.name}
                    {job.status === 'queued'     ? ' — Queued, processing soon…' : ''}
                    {job.status === 'processing' ? ' — Recognising…' : ''}
                    {job.status === 'failed' && job.error ? ` — ${job.error}` : ''}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Results */}
      {uploadResults && (
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Upload Results</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-green-50 p-4 rounded-lg">
              <p className="text-sm text-green-600 font-medium">Successfully Processed</p>
              <p className="text-3xl font-bold text-green-700">{uploadResults.processed}</p>
            </div>
            <div className="bg-red-50 p-4 rounded-lg">
              <p className="text-sm text-red-600 font-medium">Failed</p>
              <p className="text-3xl font-bold text-red-700">{uploadResults.failed}</p>
            </div>
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-sm text-blue-600 font-medium">Total Files</p>
              <p className="text-3xl font-bold text-blue-700">
                {uploadResults.processed + uploadResults.failed}
              </p>
            </div>
          </div>

          {/* Processed Invoices */}
          {uploadResults.invoices && uploadResults.invoices.length > 0 && (
            <div className="mb-6">
              <h3 className="font-semibold text-gray-900 mb-3">Processed Invoices</h3>
              <div className="space-y-2">
                {uploadResults.invoices.map((inv, idx) => {
                  const mismatch = inv.detectedType && inv.documentType && inv.detectedType !== inv.documentType;
                  return (
                  <div 
                    key={idx}
                    className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200"
                  >
                    <div>
                      <p className="font-medium text-gray-900">{inv.filename}</p>
                      <p className="text-sm text-gray-600">
                        Invoice #: {inv.invoiceNumber} | 
                        Amount: £{inv.extractedData?.totalAmount?.toFixed(2) || '0.00'}
                        {inv.documentType === 'CreditNote' && (
                          <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-teal-100 text-teal-800">Credit Note</span>
                        )}
                      </p>
                      {mismatch && (
                        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-1">
                          ⚠️ AI detected this may be a <strong>{inv.detectedType === 'CreditNote' ? 'Credit Note' : 'Invoice'}</strong>, but it was uploaded as a <strong>{inv.documentType === 'CreditNote' ? 'Credit Note' : 'Invoice'}</strong>. Please verify.
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => navigate(`/invoices/${inv.invoiceId}`)}
                      className="btn btn-primary btn-sm"
                    >
                      View Invoice
                    </button>
                  </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Errors */}
          {uploadResults.errors && uploadResults.errors.length > 0 && (
            <div>
              <h3 className="font-semibold text-red-900 mb-3">Errors</h3>
              <div className="space-y-2">
                {uploadResults.errors.map((err, idx) => (
                  <div key={idx} className="p-3 bg-red-50 rounded-lg border border-red-200">
                    <p className="font-medium text-red-900">{err.filename}</p>
                    <p className="text-sm text-red-700">{err.error}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-6">
            <button
              onClick={() => navigate('/invoices')}
              className="btn btn-primary btn-lg"
            >
              View All Invoices
            </button>
          </div>
        </div>
      )}

      {/* Info Section */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="font-semibold text-blue-900 mb-2">How it works:</h3>
        <ol className="list-decimal list-inside space-y-1 text-blue-800">
          <li>Upload one or more PDF/image invoices (up to 10 files, 10MB each)</li>
          <li>AI automatically extracts: vendor, amount, date, line items</li>
          <li>Files are saved to secure Azure Blob Storage</li>
          <li>Invoices are created in Draft status for you to review</li>
          <li>Review and approve extracted data before submitting</li>
        </ol>
        <p className="mt-3 text-sm text-blue-700 font-medium">
          Uploads run in the background — you can navigate to other pages and the progress indicator in the bottom-right will keep you updated.
        </p>
      </div>
    </div>
  );
}
